import { cn } from '@/lib/utils';

/**
 * The double-bezel frame for screenshots and photos, on the light pages (the
 * dark one went with the consulting photos, Task 4c): an outer tray, a
 * tinted shell with a hairline edge (rounded-mk-bezel, 6px padding),
 * holding an inner plate whose 22px radius stays concentric with the
 * tray's 28px. The plate's hairline and top highlight are drawn on an
 * ::after overlay rather than on the plate itself: an inset shadow paints
 * under the plate's children, so a full-bleed screenshot would cover it.
 * Pass `src` / `alt` / `width` /
 * `height` for an image (lazy unless `priority`, which also asks for it at
 * high fetch priority), or `children` to frame anything else. `shadow` lifts
 * the whole frame off the page.
 */
export default function BezelFrame({
    src,
    alt,
    width,
    height,
    priority = false,
    shadow = false,
    className,
    imgClassName,
    children,
    ...props
}) {
    return (
        <div
            className={cn('rounded-mk-bezel bg-mk-sunken p-1.5 ring-1 ring-mk-line', shadow && 'shadow-mk-lift', className)}
            {...props}
        >
            <div
                className={cn(
                    // h-full: fills the tray when a grid stretches it to a
                    // neighbour's height; a no-op when the tray sizes itself.
                    'relative h-full overflow-hidden rounded-mk-inner',
                    'after:pointer-events-none after:absolute after:inset-0 after:rounded-mk-inner after:ring-1 after:ring-inset',
                    'after:shadow-[inset_0_1px_1px_rgba(255,255,255,0.6)]',
                    'bg-mk-surface after:ring-mk-ink/5',
                )}
            >
                {children ?? (
                    <img
                        src={src}
                        alt={alt}
                        width={width}
                        height={height}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchpriority={priority ? 'high' : undefined}
                        decoding={priority ? undefined : 'async'}
                        className={cn('block h-auto w-full', imgClassName)}
                    />
                )}
            </div>
        </div>
    );
}
